import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { get, post } from "../../controllers/apiController";
import Layout from "../../components/Layout";
import { Select2WColor, Select2 } from "../../components/Select2";
import DataTable from "react-data-table-component";
import Chart from "react-apexcharts";
import * as XLSX from 'xlsx';

/* ─── Yardımcı ─── */
const fmt = n => (n ?? 0).toLocaleString('tr-TR');
const pct = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '0%';

/* ─── Tooltip ─── */
const Tooltip = ({ content, children }) => {
    const [vis, setVis] = useState(false);
    return (
        <span
            className="position-relative d-inline-block"
            onMouseEnter={() => setVis(true)}
            onMouseLeave={() => setVis(false)}
        >
            {children}
            {vis && content && (
                <div style={{
                    position: 'absolute', bottom: 'calc(100% + 6px)', left: 0,
                    background: '#1e293b', color: '#f1f5f9', padding: '8px 12px',
                    borderRadius: 8, fontSize: 12, lineHeight: 1.6,
                    whiteSpace: 'pre-wrap', maxWidth: 320, width: 'max-content',
                    zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.28)', pointerEvents: 'none'
                }}>
                    {content}
                    <div style={{
                        position: 'absolute', top: '100%', left: 14,
                        border: '5px solid transparent', borderTopColor: '#1e293b'
                    }} />
                </div>
            )}
        </span>
    );
};

/* ─── Markdown Renderer (Gemini yanıtı için) ─── */
const renderInline = (text) => {
    if (!text) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**')
            ? <strong key={i}>{p.slice(2, -2)}</strong>
            : p
    );
};

const MarkdownText = ({ text }) => {
    if (!text) return null;
    return (
        <div style={{ lineHeight: 1.85, fontSize: 14, color: '#1e293b' }}>
            {text.split('\n').map((line, i) => {
                if (line.startsWith('## '))
                    return <h5 key={i} className="fw-bold mt-4 mb-2 pb-1" style={{ color: '#0f172a', borderBottom: '2px solid #e2e8f0' }}>{line.slice(3)}</h5>;
                if (line.startsWith('### '))
                    return <h6 key={i} className="fw-semibold mt-3 mb-1" style={{ color: '#334155' }}>{line.slice(4)}</h6>;
                if (line.startsWith('# '))
                    return <h4 key={i} className="fw-bold mt-3 mb-2" style={{ color: '#0f172a' }}>{line.slice(2)}</h4>;
                if (/^(\d+)\.\s/.test(line)) {
                    const match = line.match(/^(\d+)\.\s(.*)/);
                    return (
                        <div key={i} className="d-flex gap-2 mb-2">
                            <span className="badge rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ background: '#3b82f6', color: '#fff', width: 22, height: 22, fontSize: 11 }}>
                                {match[1]}
                            </span>
                            <span>{renderInline(match[2])}</span>
                        </div>
                    );
                }
                if (line.startsWith('- ') || line.startsWith('* '))
                    return (
                        <div key={i} className="d-flex gap-2 mb-1 ms-2">
                            <span style={{ color: '#3b82f6', marginTop: 3, flexShrink: 0 }}>•</span>
                            <span>{renderInline(line.slice(2))}</span>
                        </div>
                    );
                if (line.trim() === '') return <div key={i} style={{ height: 6 }} />;
                return <p key={i} className="mb-1">{renderInline(line)}</p>;
            })}
        </div>
    );
};

const AY_ISIM = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const SOURCES = [
    { value: "Meta", label: "Meta" },
    { value: "Manuel", label: "Manuel" },
    { value: "Ads", label: "Ads" },
    { value: "Landing Page", label: "Landing Page" },
    { value: "WordPress", label: "WordPress" },
    { value: "Referans", label: "Referans" }
];

/* ─── KPI Kartı ─── */
const KpiCard = ({ icon, color, title, value, sub, badge }) => (
    <div className="col-xxl-2 col-xl-4 col-md-6">
        <div className="card stretch stretch-full border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center gap-3">
                <div className={`avatar-text avatar-xl rounded-3`} style={{ background: color + '18', color }}>
                    <i className={`feather-${icon} fs-5`}></i>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                    <p className="text-muted mb-1 small text-truncate">{title}</p>
                    <h4 className="fw-bold mb-0">{value}</h4>
                    {sub && <span className="small text-muted">{sub}</span>}
                    {badge && (
                        <span className="badge ms-1" style={{ background: color + '20', color }}>{badge}</span>
                    )}
                </div>
            </div>
        </div>
    </div>
);

/* ─── Ana Bileşen ─── */
const Reports = () => {
    /* Filtre state */
    const [labels, setLabels] = useState([]);
    const [selectedLabels, setSelectedLabels] = useState(null);
    const [selectedSources, setSelectedSources] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [lastDate, setLastDate] = useState('');

    /* Pipeline stages */
    const [pipelineStages, setPipelineStages] = useState([]);

    /* Veri state */
    const [analytics, setAnalytics] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    /* Pipeline aşama filtresi */
    const [selectedPipelineStages, setSelectedPipelineStages] = useState(null);

    /* Temsilci filtresi */
    const [selectedAgents, setSelectedAgents] = useState(null);

    /* Kampanya */
    const [campaignData, setCampaignData] = useState(null);
    const [campaignLoading, setCampaignLoading] = useState(false);
    const [expandedCampaign, setExpandedCampaign] = useState(null);

    /* AI Raporu */
    const [aiReport, setAiReport] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    /* Sekme */
    const [activeTab, setActiveTab] = useState('overview');
    const [showLeadsTable, setShowLeadsTable] = useState(false);

    /* Rapor özelleştirme */
    const REPORT_SECTIONS = [
        { key: 'kpi', label: 'KPI Özet', icon: 'feather-activity' },
        { key: 'pipeline', label: 'Pipeline Analizi', icon: 'feather-git-branch' },
        { key: 'temsilci', label: 'Temsilci Performansı', icon: 'feather-users' },
        { key: 'kaynak', label: 'Kaynak Analizi', icon: 'feather-pie-chart' },
        { key: 'sektor', label: 'Sektör Analizi', icon: 'feather-briefcase' },
        { key: 'trend', label: 'Aylık Trend', icon: 'feather-trending-up' },
        { key: 'kampanya', label: 'Kampanya Analizi', icon: 'feather-target' },
        { key: 'detail', label: 'Lead Tablosu', icon: 'feather-list' },
    ];
    const [visibleSections, setVisibleSections] = useState(() => {
        try {
            const s = localStorage.getItem('lup_report_sections');
            return s ? JSON.parse(s) : REPORT_SECTIONS.map(s => s.key);
        } catch {
            return REPORT_SECTIONS.map(s => s.key);
        }
    });
    const isSectionVisible = (key) => visibleSections.includes(key);
    const toggleSection = (key) => {
        setVisibleSections(prev => {
            const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
            localStorage.setItem('lup_report_sections', JSON.stringify(next));
            return next;
        });
    };
    const [showSectionCustomize, setShowSectionCustomize] = useState(false);

    /* ─── İlk yükleme ─── */
    useEffect(() => {
        document.title = "Raporlama | LEVELUP CRM";
        const init = async () => {
            try {
                const [psRes, lblRes, anaRes] = await Promise.all([
                    get("settings/pipeline"),
                    get("labels/"),
                    get("reports/analytics")
                ]);
                setPipelineStages(psRes?.stages || []);
                setLabels(lblRes.map(e => ({ value: e.Etiket_ID, label: e.Etiket_Adi, color: "#" + e.Etiket_Renk })));
                setAnalytics(anaRes);
            } catch (err) {
                toast.error("Veriler yüklenirken hata oluştu!");
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    /* ─── Filtreli analitik çek ─── */
    const fetchAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        try {
            const params = {};
            if (startDate) params.start = startDate;
            if (lastDate) params.end = lastDate;
            const res = await get("reports/analytics", params);
            setAnalytics(res);
        } catch {
            toast.error("Analitik veriler alınamadı.");
        } finally {
            setAnalyticsLoading(false);
        }
    }, [startDate, lastDate]);

    /* ─── Detaylı lead tablosu ─── */
    const fetchLeads = async () => {
        try {
            const payload = {
                startDate: startDate || null,
                lastDate: lastDate || null,
                sources: selectedSources?.map(s => s.value) ?? [],
                ...(selectedLabels?.length ? { labelIds: selectedLabels.map(l => l.value) } : {})
            };
            const res = await post("reports/", payload);
            setLeads(res);
            setShowLeadsTable(true);
            setActiveTab('detail');
        } catch (err) {
            toast.error("Rapor oluşturulurken hata oluştu.");
        }
    };

    /* ─── Lead tablosu (filtreli) ─── */
    const filteredLeads = leads.filter(l => {
        if (selectedPipelineStages?.length) {
            const stageMatch = selectedPipelineStages.some(s => Number(s.value) === Number(l.LeadInfo?.Pipeline_Asama_ID));
            if (!stageMatch) return false;
        }
        if (selectedAgents?.length) {
            const agentMatch = selectedAgents.some(a => a.label === (l.Temsilci_Isim || '').trim());
            if (!agentMatch) return false;
        }
        return true;
    });

    /* ─── Kampanya analizi ─── */
    const fetchCampaignAnalysis = useCallback(async () => {
        setCampaignLoading(true);
        try {
            const params = {};
            if (startDate) params.start = startDate;
            if (lastDate) params.end = lastDate;
            if (selectedSources?.length === 1) params.source = selectedSources[0].value;
            const res = await get("reports/campaign-analysis", params);
            setCampaignData(res.campaigns);
            setExpandedCampaign(null);
            setActiveTab('campaign');
        } catch {
            toast.error("Kampanya analizi yüklenemedi.");
        } finally {
            setCampaignLoading(false);
        }
    }, [startDate, lastDate, selectedSources]);

    /* ─── Gemini AI Raporu ─── */
    const generateAiReport = useCallback(async () => {
        setAiLoading(true);
        setActiveTab('ai');
        try {
            const res = await post("reports/ai-analysis", {
                startDate: startDate || null,
                endDate: lastDate || null
            });
            setAiReport(res.report);
        } catch (err) {
            const detail = err?.response?.data?.error || err?.response?.data || err?.message || '';
            toast.error("AI raporu oluşturulamadı." + (detail ? ` Hata: ${detail}` : ''));
            setAiReport(null);
        } finally {
            setAiLoading(false);
        }
    }, [startDate, lastDate]);

    /* ─── Hızlı tarih seçimi ─── */
    const setQuickRange = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setStartDate(start.toISOString().split('T')[0]);
        setLastDate(end.toISOString().split('T')[0]);
    };

    /* ─── Excel ─── */
    const excelDownload = () => {
        const excelData = filteredLeads.map(row => {
            const stage = pipelineStages.find(s => Number(s.Asama_ID) === Number(row.LeadInfo?.Pipeline_Asama_ID));
            return {
                'ADI SOYADI': row.LeadInfo.Adi_Soyadi,
                'E-POSTA': row.LeadInfo.E_Posta,
                'TELEFON': row.LeadInfo.Telefon,
                'KAYNAK': row.LeadInfo.Kaynak,
                'PİPELİNE AŞAMASI': stage?.Ad || '',
                'SEKTÖR': row.LeadInfo.Sektor || '',
                'META KAMPANYA': row.LeadInfo.Kampanya,
                'UTM SOURCE': row.LeadInfo.UTM_Source,
                'UTM KAMPANYA': row.LeadInfo.UTM_Campaign,
                'UTM KELİME': row.LeadInfo.UTM_Kelime,
                'TARİH': row.LeadInfo.Tarih,
                'ETİKET': row.Etiketler.map(l => l.Etiket_Adi).join(', '),
                'TEMSİLCİ': row.Temsilci_Isim !== "null null" ? row.Temsilci_Isim : "Yok",
                'IP': row.LeadInfo.IP,
                'MESAJ': row.LeadInfo.Mesaj,
                'AÇIKLAMA': row.LeadInfo.Aciklama,
            };
        });
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Formlar");
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rapor_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    /* ════════════ CHART CONFIG'LARI ════════════ */

    /* Aylık Trend */
    const trendOptions = {
        chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
        colors: ['#3b82f6', '#10b981'],
        fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
        stroke: { curve: 'smooth', width: 2 },
        dataLabels: { enabled: false },
        xaxis: {
            categories: analytics?.monthlyTrend?.map(d => {
                const [year, mon] = d.ay.split('-');
                return AY_ISIM[parseInt(mon) - 1] + ' ' + year.slice(2);
            }) ?? [],
            labels: { style: { fontSize: '11px' } }
        },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        tooltip: { x: { show: true } },
        legend: { position: 'top', horizontalAlign: 'right' },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 }
    };
    const trendSeries = [
        { name: 'Toplam Lead', data: analytics?.monthlyTrend?.map(d => d.toplam_lead) ?? [] },
        { name: 'Satış', data: analytics?.monthlyTrend?.map(d => d.satis) ?? [] }
    ];

    /* Kaynak Dağılımı */
    const kaynakOptions = {
        chart: { type: 'donut', fontFamily: 'inherit' },
        labels: analytics?.sourceBreakdown?.map(s => s.kaynak) ?? [],
        colors: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899'],
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: { style: { fontSize: '11px' } },
        plotOptions: { pie: { donut: { size: '65%', labels: { show: true, total: { show: true, label: 'Toplam', fontSize: '14px', fontWeight: 600 } } } } },
        tooltip: { y: { formatter: v => v + ' lead' } }
    };
    const kaynakSeries = analytics?.sourceBreakdown?.map(s => Number(s.toplam)) ?? [];

    /* Pipeline Hunisi */
    const huniLabels = ['Toplam Lead', 'Arandı', 'Ulaşıldı', 'Toplantı', 'Teklif', 'Satış'];
    const huniValues = analytics?.pipelineFunnel ? [
        Number(analytics.pipelineFunnel.toplam_lead),
        Number(analytics.pipelineFunnel.arandi),
        Number(analytics.pipelineFunnel.ulasildi),
        Number(analytics.pipelineFunnel.toplanti),
        Number(analytics.pipelineFunnel.teklif),
        Number(analytics.pipelineFunnel.satis)
    ] : [];
    const huniOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, dataLabels: { position: 'top' } } },
        colors: ['#3b82f6'],
        dataLabels: {
            enabled: true,
            formatter: (val, { dataPointIndex }) => {
                const base = huniValues[0];
                return val + (base > 0 ? '  (' + ((val / base) * 100).toFixed(0) + '%)' : '');
            },
            offsetX: 8,
            style: { fontSize: '11px', colors: ['#374151'] }
        },
        xaxis: { categories: huniLabels, labels: { style: { fontSize: '11px' } } },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        tooltip: { y: { formatter: v => fmt(v) + ' lead' } }
    };
    const huniSeries = [{ name: 'Adet', data: huniValues }];

    /* Temsilci Performansı */
    const temsilciOptions = {
        chart: { type: 'bar', toolbar: { show: false }, stacked: false, fontFamily: 'inherit' },
        plotOptions: { bar: { horizontal: false, borderRadius: 3, columnWidth: '55%' } },
        colors: ['#3b82f6', '#10b981', '#f59e0b'],
        dataLabels: { enabled: false },
        xaxis: {
            categories: analytics?.agentPerformance?.map(a => a.temsilci) ?? [],
            labels: { style: { fontSize: '11px' }, rotate: -30 }
        },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        legend: { position: 'top' },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        tooltip: { shared: true, intersect: false }
    };
    const temsilciSeries = [
        { name: 'Atanan Lead', data: analytics?.agentPerformance?.map(a => Number(a.atanan_lead)) ?? [] },
        { name: 'Teklif', data: analytics?.agentPerformance?.map(a => Number(a.teklif)) ?? [] },
        { name: 'Satış', data: analytics?.agentPerformance?.map(a => Number(a.satis)) ?? [] }
    ];

    /* Haftanın Günleri */
    const gunOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        colors: ['#6366f1'],
        dataLabels: { enabled: false },
        xaxis: {
            categories: analytics?.dayOfWeek?.map(d => d.gun) ?? [],
            labels: { style: { fontSize: '11px' } }
        },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        tooltip: { y: { formatter: v => v + ' lead' } }
    };
    const gunSeries = [{ name: 'Lead', data: analytics?.dayOfWeek?.map(d => d.toplam) ?? [] }];

    /* Kampanya Grafiği */
    const topCampaigns = (campaignData || []).slice(0, 12);
    const kampanyaChartOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '60%', dataLabels: { position: 'top' } } },
        colors: ['#3b82f6', '#10b981'],
        dataLabels: { enabled: false },
        xaxis: { labels: { style: { fontSize: '11px' } } },
        yaxis: { categories: topCampaigns.map(c => (c.kampanya || 'Bilinmiyor').substring(0, 35)), labels: { style: { fontSize: '11px' } } },
        legend: { position: 'top' },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        tooltip: { y: { formatter: v => fmt(v) + ' lead' } }
    };
    const kampanyaChartSeries = [
        { name: 'Toplam Lead', data: topCampaigns.map(c => c.toplam) },
        { name: 'Satış', data: topCampaigns.map(c => c.satis) }
    ];

    /* Lead tablosu kolonları */
    const columns = [
        {
            name: 'ADI SOYADI',
            cell: row => (
                <a href={`/lead-detail?id=${row.Form_ID}`} className="fw-semibold text-dark text-decoration-none">
                    {row.LeadInfo.Adi_Soyadi}
                </a>
            ),
            sortable: true,
            grow: 1.5
        },
        { name: 'TELEFON', cell: row => <a href={`tel:${row.LeadInfo.Telefon}`} className="text-muted text-decoration-none">{row.LeadInfo.Telefon}</a> },
        { name: 'KAYNAK', cell: row => <span className="badge bg-light text-dark border">{row.LeadInfo.Kaynak}</span> },
        {
            name: 'TARİH',
            cell: row => {
                const d = new Date(row.LeadInfo.Tarih);
                return <span className="text-muted small">{`${d.getDate().toString().padStart(2,'0')}.${(d.getMonth()+1).toString().padStart(2,'0')}.${d.getFullYear()}`}</span>;
            },
            sortable: true
        },
        {
            name: 'PİPELİNE AŞAMASI',
            cell: row => {
                const stage = pipelineStages.find(s => Number(s.Asama_ID) === Number(row.LeadInfo?.Pipeline_Asama_ID));
                if (stage) return (
                    <span className="badge" style={{ background: (stage.Renk || '#6366f1') + '20', color: stage.Renk || '#6366f1', border: `1px solid ${(stage.Renk || '#6366f1')}35`, fontSize: 11 }}>
                        {stage.Ad}
                    </span>
                );
                return <span className="text-muted small">—</span>;
            }
        },
        {
            name: 'SEKTÖR',
            cell: row => row.LeadInfo?.Sektor ? <span className="badge bg-light text-dark border">{row.LeadInfo.Sektor}</span> : <span className="text-muted">—</span>
        },
        {
            name: 'ETİKET',
            cell: row => (
                <div className="d-flex gap-1 flex-wrap">
                    {row.Etiketler?.slice(0, 2).map((e, i) => (
                        <span key={i} className="badge" style={{ background: '#' + e.Etiket_Renk, color: '#' + e.Etiket_Yazi_Renk, fontSize: 10 }}>
                            {e.Etiket_Adi}
                        </span>
                    ))}
                    {row.Etiketler?.length > 2 && <span className="badge bg-secondary" style={{ fontSize: 10 }}>+{row.Etiketler.length - 2}</span>}
                </div>
            ),
            grow: 1.5
        },
        { name: 'TEMSİLCİ', cell: row => <span className="small">{row.Temsilci_Isim !== "null null" ? row.Temsilci_Isim : "—"}</span> }
    ];

    const paginationOpts = {
        rowsPerPageText: 'Satır:',
        rangeSeparatorText: '/',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Tümü'
    };

    /* ════════════ RENDER ════════════ */
    if (loading) {
        return (
            <Layout>
                <main className="nxl-container">
                    <div className="nxl-content d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" role="status"></div>
                            <p className="text-muted">Raporlar yükleniyor...</p>
                        </div>
                    </div>
                </main>
            </Layout>
        );
    }

    const totals = analytics?.totals ?? {};
    const funnel = analytics?.pipelineFunnel ?? {};
    const donusumOrani = totals.toplam_lead > 0 ? ((totals.toplam_satis / totals.toplam_lead) * 100).toFixed(1) : 0;
    const teklifOrani = totals.toplam_lead > 0 ? ((totals.toplam_teklif / totals.toplam_lead) * 100).toFixed(1) : 0;

    // Pipeline-based KPI hesapları
    const pipelineBreakdown = analytics?.pipelineBreakdown ?? [];
    const kazanildiCount = pipelineBreakdown.filter(s => s.Tur === 'kazanildi').reduce((acc, s) => acc + Number(s.lead_sayisi), 0);
    const kaybedildiCount = pipelineBreakdown.filter(s => s.Tur === 'kaybedildi').reduce((acc, s) => acc + Number(s.lead_sayisi), 0);
    const pipelineTotal = pipelineBreakdown.reduce((acc, s) => acc + Number(s.lead_sayisi), 0);
    const noStageCount = analytics?.noStageCount || 0;

    return (
        <Layout>
            <main className="nxl-container">
                <div className="nxl-content">
                    {/* ── Page Header ── */}
                    <div className="page-header">
                        <div className="page-header-left d-flex align-items-center">
                            <div className="page-header-title">
                                <h5 className="m-b-10">LEVELUP CRM</h5>
                            </div>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item"><a href="/dashboard">Anasayfa</a></li>
                                <li className="breadcrumb-item">Raporlama</li>
                            </ul>
                        </div>
                        <div className="page-header-right ms-auto">
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setShowSectionCustomize(v => !v)}
                            >
                                <i className="feather-sliders me-1"></i>Rapor Özelleştir
                            </button>
                        </div>
                    </div>

                    {/* Rapor Bölümü Özelleştirme Paneli */}
                    {showSectionCustomize && (
                        <div className="card border-0 shadow-sm mb-3">
                            <div className="card-header d-flex align-items-center justify-content-between py-2">
                                <h6 className="mb-0 fw-semibold">
                                    <i className="feather-sliders me-2 text-primary"></i>Rapor Bölümleri
                                </h6>
                                <button className="btn btn-sm btn-link p-0" onClick={() => setShowSectionCustomize(false)}>
                                    <i className="feather-x"></i>
                                </button>
                            </div>
                            <div className="card-body py-2">
                                <div className="row g-2">
                                    {REPORT_SECTIONS.map(sec => (
                                        <div key={sec.key} className="col-6 col-md-3">
                                            <div
                                                className={`d-flex align-items-center gap-2 p-2 rounded-2 border ${isSectionVisible(sec.key) ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary bg-light'}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => toggleSection(sec.key)}
                                            >
                                                <i className={`${sec.icon} small ${isSectionVisible(sec.key) ? 'text-primary' : 'text-muted'}`}></i>
                                                <span className="small fw-medium">{sec.label}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="main-content">
                        {/* ── Filtre Kartı ── */}
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body p-3">
                                <div className="row g-3 align-items-end">
                                    <div className="col-12 col-md-auto">
                                        <label className="form-label small fw-semibold mb-1">Hızlı Seçim</label>
                                        <div className="btn-group btn-group-sm">
                                            {[
                                                { label: 'Bu Ay', fn: () => { const n = new Date(); setStartDate(new Date(n.getFullYear(), n.getMonth(), 1).toISOString().split('T')[0]); setLastDate(n.toISOString().split('T')[0]); } },
                                                { label: '30 Gün', fn: () => setQuickRange(30) },
                                                { label: '90 Gün', fn: () => setQuickRange(90) },
                                                { label: '6 Ay', fn: () => setQuickRange(180) },
                                                { label: 'Tüm Zaman', fn: () => { setStartDate(''); setLastDate(''); } }
                                            ].map(b => (
                                                <button key={b.label} type="button" className="btn btn-outline-secondary" onClick={b.fn}>
                                                    {b.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-2">
                                        <label className="form-label small fw-semibold mb-1">Başlangıç</label>
                                        <input type="date" className="form-control form-control-sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
                                    </div>
                                    <div className="col-6 col-md-2">
                                        <label className="form-label small fw-semibold mb-1">Bitiş</label>
                                        <input type="date" className="form-control form-control-sm" value={lastDate} onChange={e => setLastDate(e.target.value)} />
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <label className="form-label small fw-semibold mb-1">Etiket</label>
                                        <Select2WColor
                                            value={selectedLabels}
                                            options={labels}
                                            isSearchable isClearable isMulti
                                            onChange={setSelectedLabels}
                                            style={{ padding: 0 }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-2">
                                        <label className="form-label small fw-semibold mb-1">Kaynak</label>
                                        <Select2
                                            value={selectedSources}
                                            options={SOURCES}
                                            isSearchable isClearable isMulti
                                            onChange={setSelectedSources}
                                        />
                                    </div>
                                    {pipelineStages.length > 0 && (
                                        <div className="col-12 col-md-2">
                                            <label className="form-label small fw-semibold mb-1">Pipeline Aşama</label>
                                            <Select2
                                                value={selectedPipelineStages}
                                                options={pipelineStages.map(s => ({ value: s.Asama_ID, label: s.Ad }))}
                                                isSearchable isClearable isMulti
                                                onChange={setSelectedPipelineStages}
                                            />
                                        </div>
                                    )}
                                    {analytics?.agentPerformance?.length > 0 && (
                                        <div className="col-12 col-md-2">
                                            <label className="form-label small fw-semibold mb-1">Temsilci</label>
                                            <Select2
                                                value={selectedAgents}
                                                options={analytics.agentPerformance.map(a => ({ value: a.temsilci, label: a.temsilci }))}
                                                isSearchable isClearable isMulti
                                                onChange={setSelectedAgents}
                                            />
                                        </div>
                                    )}
                                    <div className="col-12 col-md-auto d-flex gap-2">
                                        <button className="btn btn-primary btn-sm" onClick={fetchAnalytics} disabled={analyticsLoading}>
                                            {analyticsLoading
                                                ? <><span className="spinner-border spinner-border-sm me-1"></span>Yükleniyor</>
                                                : <><i className="feather-bar-chart-2 me-1"></i>Analiz</>
                                            }
                                        </button>
                                        <button className="btn btn-outline-primary btn-sm" onClick={fetchLeads}>
                                            <i className="feather-list me-1"></i>Lead Listesi
                                        </button>
                                        <button className="btn btn-outline-warning btn-sm" onClick={fetchCampaignAnalysis} disabled={campaignLoading}>
                                            {campaignLoading
                                                ? <><span className="spinner-border spinner-border-sm me-1"></span>Yükleniyor</>
                                                : <><i className="feather-target me-1"></i>Kampanya</>
                                            }
                                        </button>
                                        <button className="btn btn-sm" onClick={generateAiReport} disabled={aiLoading}
                                            style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none' }}>
                                            {aiLoading
                                                ? <><span className="spinner-border spinner-border-sm me-1"></span>Gemini...</>
                                                : <><i className="feather-zap me-1"></i>AI Raporu</>
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Sekmeler ── */}
                        <ul className="nav nav-tabs mb-4">
                            <li className="nav-item">
                                <button className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                                    <i className="feather-grid me-1"></i>Genel Bakış
                                </button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link ${activeTab === 'pipeline' ? 'active' : ''}`} onClick={() => setActiveTab('pipeline')}>
                                    <i className="feather-filter me-1"></i>Pipeline
                                </button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link ${activeTab === 'agents' ? 'active' : ''}`} onClick={() => setActiveTab('agents')}>
                                    <i className="feather-users me-1"></i>Temsilciler
                                </button>
                            </li>
                            {analytics?.sektorPerf?.length > 0 && (
                                <li className="nav-item">
                                    <button className={`nav-link ${activeTab === 'sektor' ? 'active' : ''}`} onClick={() => setActiveTab('sektor')}>
                                        <i className="feather-briefcase me-1"></i>Sektör
                                    </button>
                                </li>
                            )}
                            {showLeadsTable && (
                                <li className="nav-item">
                                    <button className={`nav-link ${activeTab === 'detail' ? 'active' : ''}`} onClick={() => setActiveTab('detail')}>
                                        <i className="feather-table me-1"></i>Lead Listesi
                                        <span className="badge bg-primary ms-1">{leads.length}</span>
                                    </button>
                                </li>
                            )}
                            {campaignData && (
                                <li className="nav-item">
                                    <button className={`nav-link ${activeTab === 'campaign' ? 'active' : ''}`} onClick={() => setActiveTab('campaign')}>
                                        <i className="feather-target me-1"></i>Kampanya Analizi
                                        <span className="badge bg-warning text-dark ms-1">{campaignData.length}</span>
                                    </button>
                                </li>
                            )}
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'ai' ? 'active' : ''}`}
                                    onClick={() => { if (aiReport || aiLoading) setActiveTab('ai'); else generateAiReport(); }}
                                    style={activeTab === 'ai' ? {} : { color: '#8b5cf6' }}
                                >
                                    <i className="feather-zap me-1"></i>
                                    {aiLoading ? 'Gemini yazıyor...' : 'AI Raporu'}
                                    {aiLoading && <span className="spinner-border spinner-border-sm ms-1" style={{ width: 10, height: 10 }}></span>}
                                </button>
                            </li>
                        </ul>

                        {/* ════ GENEL BAKIŞ ════ */}
                        {activeTab === 'overview' && (
                            <>
                                {/* KPI Kartları */}
                                {isSectionVisible('kpi') && (
                                    <div className="row g-3 mb-4">
                                        <KpiCard icon="users" color="#3b82f6" title="Toplam Lead"
                                            value={fmt(totals.toplam_lead)} />
                                        <KpiCard icon="check-circle" color="#10b981" title="Kazanılan"
                                            value={fmt(kazanildiCount || totals.toplam_satis)}
                                            badge={`%${donusumOrani} dönüşüm`} />
                                        <KpiCard icon="x-circle" color="#ef4444" title="Kaybedilen"
                                            value={fmt(kaybedildiCount || totals.kayip_lead)} />
                                        <KpiCard icon="git-branch" color="#6366f1" title="Pipeline'da"
                                            value={fmt(pipelineTotal)}
                                            sub={noStageCount > 0 ? `${noStageCount} atanmamış` : undefined} />
                                        <KpiCard icon="file-text" color="#8b5cf6" title="Teklif Verilen"
                                            value={fmt(totals.toplam_teklif)}
                                            badge={`%${teklifOrani}`} />
                                        <KpiCard icon="clock" color="#f59e0b" title="Bekleyen Lead"
                                            value={fmt(totals.bekleyen_lead)}
                                            sub="Henüz aranmadı" />
                                        <KpiCard icon="calendar" color="#06b6d4" title="Ort. Satış Süresi"
                                            value={totals.ort_satis_gun ? Math.round(totals.ort_satis_gun) + ' gün' : '—'}
                                            sub="Lead → Satış" />
                                    </div>
                                )}

                                {/* Trend + Kaynak */}
                                <div className="row g-4 mb-4">
                                    <div className="col-xl-8">
                                        <div className="card border-0 shadow-sm h-100">
                                            <div className="card-header bg-white border-bottom-0 pb-0">
                                                <h6 className="card-title mb-0 fw-semibold">
                                                    <i className="feather-trending-up text-primary me-2"></i>
                                                    Aylık Lead & Satış Trendi
                                                    <small className="text-muted fw-normal ms-2">(Son 12 ay)</small>
                                                </h6>
                                            </div>
                                            <div className="card-body pt-2">
                                                {analytics?.monthlyTrend?.length > 0
                                                    ? <Chart options={trendOptions} series={trendSeries} type="area" height={280} />
                                                    : <div className="text-center text-muted py-5">Veri bulunamadı</div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-4">
                                        <div className="card border-0 shadow-sm h-100">
                                            <div className="card-header bg-white border-bottom-0 pb-0">
                                                <h6 className="card-title mb-0 fw-semibold">
                                                    <i className="feather-pie-chart text-purple me-2"></i>
                                                    Kaynak Dağılımı
                                                </h6>
                                            </div>
                                            <div className="card-body pt-2">
                                                {kaynakSeries.length > 0
                                                    ? <Chart options={kaynakOptions} series={kaynakSeries} type="donut" height={280} />
                                                    : <div className="text-center text-muted py-5">Veri bulunamadı</div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Günlük Dağılım + Kaynak Tablo */}
                                <div className="row g-4">
                                    <div className="col-xl-5">
                                        <div className="card border-0 shadow-sm h-100">
                                            <div className="card-header bg-white border-bottom-0 pb-0">
                                                <h6 className="card-title mb-0 fw-semibold">
                                                    <i className="feather-calendar text-indigo me-2"></i>
                                                    Haftanın Günlerine Göre Lead
                                                </h6>
                                            </div>
                                            <div className="card-body pt-2">
                                                {analytics?.dayOfWeek?.some(d => d.toplam > 0)
                                                    ? <Chart options={gunOptions} series={gunSeries} type="bar" height={240} />
                                                    : <div className="text-center text-muted py-5">Veri bulunamadı</div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-7">
                                        <div className="card border-0 shadow-sm h-100">
                                            <div className="card-header bg-white border-bottom-0 pb-0">
                                                <h6 className="card-title mb-0 fw-semibold">
                                                    <i className="feather-zap text-warning me-2"></i>
                                                    Kaynak Bazlı Dönüşüm Analizi
                                                </h6>
                                            </div>
                                            <div className="card-body p-0">
                                                <div className="table-responsive">
                                                    <table className="table table-hover mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th className="ps-4 py-3 fw-semibold small">KAYNAK</th>
                                                                <th className="py-3 fw-semibold small text-end">LEAD</th>
                                                                <th className="py-3 fw-semibold small text-end">SATIŞ</th>
                                                                <th className="pe-4 py-3 fw-semibold small text-end">DÖNÜŞÜM</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {analytics?.sourceBreakdown?.map((s, i) => (
                                                                <tr key={i}>
                                                                    <td className="ps-4 py-3">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <div className="rounded-circle" style={{
                                                                                width: 8, height: 8,
                                                                                background: ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#6366f1','#ec4899'][i % 7]
                                                                            }}></div>
                                                                            <span className="fw-medium">{s.kaynak}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-3 text-end">{fmt(s.toplam)}</td>
                                                                    <td className="py-3 text-end text-success fw-semibold">{fmt(s.satis)}</td>
                                                                    <td className="pe-4 py-3 text-end">
                                                                        <span className="badge"
                                                                            style={{
                                                                                background: s.toplam > 0 && s.satis / s.toplam > 0.1 ? '#dcfce7' : '#fef9c3',
                                                                                color: s.toplam > 0 && s.satis / s.toplam > 0.1 ? '#166534' : '#854d0e'
                                                                            }}>
                                                                            %{pct(s.satis, s.toplam)}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ════ PİPELINE ════ */}
                        {activeTab === 'pipeline' && isSectionVisible('pipeline') && (
                            <>
                                {/* Aşama Kartları */}
                                {pipelineBreakdown.length > 0 ? (
                                    <>
                                        <div className="row g-3 mb-4">
                                            {pipelineBreakdown.map((stage, i) => {
                                                const total = pipelineBreakdown.reduce((acc, s) => acc + Number(s.lead_sayisi), 0);
                                                const ratio = total > 0 ? ((Number(stage.lead_sayisi) / total) * 100).toFixed(1) : 0;
                                                return (
                                                    <div key={i} className="col">
                                                        <div className="card border-0 shadow-sm text-center py-3" style={{ borderTop: `3px solid ${stage.Renk || '#6366f1'}` }}>
                                                            <div className="fs-4 fw-bold" style={{ color: stage.Renk || '#6366f1' }}>{fmt(stage.lead_sayisi)}</div>
                                                            <div className="small text-muted mt-1">{stage.Ad}</div>
                                                            <div className="small fw-semibold" style={{ color: stage.Renk || '#6366f1' }}>%{ratio}</div>
                                                            {stage.Tur === 'kazanildi' && <div className="small text-success mt-1"><i className="feather-check-circle"></i></div>}
                                                            {stage.Tur === 'kaybedildi' && <div className="small text-danger mt-1"><i className="feather-x-circle"></i></div>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Pipeline Bar Chart */}
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-header bg-white">
                                                <h6 className="card-title mb-0 fw-semibold">
                                                    <i className="feather-git-branch text-primary me-2"></i>
                                                    Pipeline Aşama Dağılımı
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <Chart
                                                    options={{
                                                        chart: { toolbar: { show: false }, fontFamily: 'inherit' },
                                                        xaxis: { categories: pipelineBreakdown.map(s => s.Ad), labels: { style: { fontSize: '11px' } } },
                                                        colors: pipelineBreakdown.map(s => s.Renk || '#6366f1'),
                                                        plotOptions: { bar: { distributed: true, borderRadius: 6, columnWidth: '55%' } },
                                                        legend: { show: false },
                                                        dataLabels: { enabled: true, style: { fontSize: '11px', fontWeight: 600 } },
                                                        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
                                                        tooltip: { y: { formatter: v => fmt(v) + ' lead' } }
                                                    }}
                                                    series={[{ name: 'Lead Sayısı', data: pipelineBreakdown.map(s => Number(s.lead_sayisi)) }]}
                                                    type="bar"
                                                    height={300}
                                                />
                                            </div>
                                        </div>

                                        {/* Temsilci × Pipeline Matrisi */}
                                        {analytics?.agentPipelinePerf?.length > 0 && (
                                            <div className="card border-0 shadow-sm mb-4">
                                                <div className="card-header bg-white">
                                                    <h6 className="card-title mb-0 fw-semibold">
                                                        <i className="feather-users text-primary me-2"></i>
                                                        Temsilci × Pipeline Matrisi
                                                    </h6>
                                                </div>
                                                <div className="card-body p-0">
                                                    <div className="table-responsive">
                                                        <table className="table table-hover mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th className="ps-4 py-3 fw-semibold small">TEMSİLCİ</th>
                                                                    {pipelineBreakdown.map(stage => (
                                                                        <th key={stage.Asama_ID} className="py-3 fw-semibold small text-center">
                                                                            <span className="badge" style={{ background: stage.Renk || '#6366f1', color: '#fff', fontSize: '10px' }}>
                                                                                {stage.Ad}
                                                                            </span>
                                                                        </th>
                                                                    ))}
                                                                    <th className="pe-4 py-3 fw-semibold small text-end">TOPLAM</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {analytics.agentPipelinePerf.map((agent, i) => (
                                                                    <tr key={i}>
                                                                        <td className="ps-4 py-3 fw-medium">{agent.temsilci}</td>
                                                                        {pipelineBreakdown.map(stage => (
                                                                            <td key={stage.Asama_ID} className="py-3 text-center">
                                                                                {agent.stages[stage.Asama_ID] || 0}
                                                                            </td>
                                                                        ))}
                                                                        <td className="pe-4 py-3 text-end fw-bold text-primary">{agent.toplam}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Sektör × Pipeline Matrisi */}
                                        {analytics?.sektorPerf?.length > 0 && (
                                            <div className="card border-0 shadow-sm mb-4">
                                                <div className="card-header bg-white">
                                                    <h6 className="card-title mb-0 fw-semibold">
                                                        <i className="feather-briefcase text-warning me-2"></i>
                                                        Sektör × Pipeline Matrisi
                                                    </h6>
                                                </div>
                                                <div className="card-body p-0">
                                                    <div className="table-responsive">
                                                        <table className="table table-hover mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th className="ps-4 py-3 fw-semibold small">SEKTÖR</th>
                                                                    {pipelineBreakdown.map(stage => (
                                                                        <th key={stage.Asama_ID} className="py-3 fw-semibold small text-center">
                                                                            <span className="badge" style={{ background: stage.Renk || '#6366f1', color: '#fff', fontSize: '10px' }}>
                                                                                {stage.Ad}
                                                                            </span>
                                                                        </th>
                                                                    ))}
                                                                    <th className="pe-4 py-3 fw-semibold small text-end">TOPLAM</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {analytics.sektorPerf.map((sektor, i) => (
                                                                    <tr key={i}>
                                                                        <td className="ps-4 py-3 fw-medium">{sektor.sektor}</td>
                                                                        {pipelineBreakdown.map(stage => (
                                                                            <td key={stage.Asama_ID} className="py-3 text-center">
                                                                                {sektor.stages[stage.Asama_ID] || 0}
                                                                            </td>
                                                                        ))}
                                                                        <td className="pe-4 py-3 text-end fw-bold text-primary">{sektor.toplam}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* Fallback: eski huni görünümü */}
                                        <div className="row g-3 mb-4">
                                            {[
                                                { label: 'Toplam Lead', value: fmt(funnel.toplam_lead), color: '#3b82f6' },
                                                { label: 'Arandı', value: fmt(funnel.arandi), color: '#8b5cf6', sub: pct(funnel.arandi, funnel.toplam_lead) },
                                                { label: 'Ulaşıldı', value: fmt(funnel.ulasildi), color: '#06b6d4', sub: pct(funnel.ulasildi, funnel.toplam_lead) },
                                                { label: 'Toplantı', value: fmt(funnel.toplanti), color: '#f59e0b', sub: pct(funnel.toplanti, funnel.toplam_lead) },
                                                { label: 'Teklif', value: fmt(funnel.teklif), color: '#10b981', sub: pct(funnel.teklif, funnel.toplam_lead) },
                                                { label: 'Satış', value: fmt(funnel.satis), color: '#22c55e', sub: pct(funnel.satis, funnel.toplam_lead) },
                                                { label: 'Kaybedilen', value: fmt(funnel.kayip), color: '#ef4444', sub: pct(funnel.kayip, funnel.toplam_lead) },
                                            ].map((item, i) => (
                                                <div key={i} className="col">
                                                    <div className="card border-0 shadow-sm text-center py-3">
                                                        <div className="fs-4 fw-bold" style={{ color: item.color }}>{item.value}</div>
                                                        <div className="small text-muted mt-1">{item.label}</div>
                                                        {item.sub && <div className="small fw-semibold" style={{ color: item.color }}>{item.sub}</div>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white">
                                                <h6 className="card-title mb-0 fw-semibold">
                                                    <i className="feather-filter text-primary me-2"></i>
                                                    Satış Hunisi — Aşama Bazlı Dönüşüm
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <Chart options={huniOptions} series={huniSeries} type="bar" height={320} />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* ════ TEMSİLCİLER ════ */}
                        {activeTab === 'agents' && isSectionVisible('temsilci') && (
                            <>
                                {/* Temsilci Performans Kartları */}
                                {analytics?.agentPerformance?.length > 0 ? (
                                    <>
                                        <div className="row g-3 mb-4">
                                            {[...analytics.agentPerformance]
                                                .sort((a, b) => b.satis - a.satis)
                                                .map((agent, i) => {
                                                    const conv = agent.atanan_lead > 0 ? ((agent.satis / agent.atanan_lead) * 100).toFixed(1) : 0;
                                                    const tekOr = agent.atanan_lead > 0 ? ((agent.teklif / agent.atanan_lead) * 100).toFixed(1) : 0;
                                                    const MEDAL = ['#f59e0b', '#9ca3af', '#cd7c32'];
                                                    return (
                                                        <div key={i} className="col-xl-3 col-md-6">
                                                            <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid ${MEDAL[i] || '#e2e8f0'}` }}>
                                                                <div className="card-body">
                                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                                        <div>
                                                                            <div className="fw-bold">{agent.temsilci}</div>
                                                                            <div className="small text-muted">{fmt(agent.atanan_lead)} lead atandı</div>
                                                                        </div>
                                                                        <div className="fw-bold fs-4" style={{ color: MEDAL[i] || '#6b7280' }}>
                                                                            #{i + 1}
                                                                        </div>
                                                                    </div>
                                                                    <div className="row g-2 mb-3">
                                                                        <div className="col-4 text-center">
                                                                            <div className="fw-bold text-primary">{fmt(agent.atanan_lead)}</div>
                                                                            <div className="text-muted" style={{ fontSize: 10 }}>Lead</div>
                                                                        </div>
                                                                        <div className="col-4 text-center">
                                                                            <div className="fw-bold text-warning">{fmt(agent.teklif)}</div>
                                                                            <div className="text-muted" style={{ fontSize: 10 }}>Teklif</div>
                                                                        </div>
                                                                        <div className="col-4 text-center">
                                                                            <div className="fw-bold text-success">{fmt(agent.satis)}</div>
                                                                            <div className="text-muted" style={{ fontSize: 10 }}>Satış</div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <div className="d-flex justify-content-between mb-1">
                                                                            <span className="small text-muted">Teklif Oranı</span>
                                                                            <span className="small fw-semibold text-primary">%{tekOr}</span>
                                                                        </div>
                                                                        <div className="progress" style={{ height: 5, borderRadius: 3 }}>
                                                                            <div className="progress-bar bg-primary" style={{ width: Math.min(tekOr, 100) + '%', borderRadius: 3 }}></div>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className="d-flex justify-content-between mb-1">
                                                                            <span className="small text-muted">Dönüşüm Oranı</span>
                                                                            <span className={`small fw-semibold ${conv >= 10 ? 'text-success' : conv >= 5 ? 'text-warning' : 'text-danger'}`}>%{conv}</span>
                                                                        </div>
                                                                        <div className="progress" style={{ height: 5, borderRadius: 3 }}>
                                                                            <div className={`progress-bar ${conv >= 10 ? 'bg-success' : conv >= 5 ? 'bg-warning' : 'bg-danger'}`}
                                                                                style={{ width: Math.min(conv, 100) + '%', borderRadius: 3 }}></div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>

                                        {/* Bar Grafik */}
                                        <div className="row g-4 mb-4">
                                            <div className="col-xl-8">
                                                <div className="card border-0 shadow-sm h-100">
                                                    <div className="card-header bg-white">
                                                        <h6 className="card-title mb-0 fw-semibold">
                                                            <i className="feather-bar-chart text-primary me-2"></i>
                                                            Lead / Teklif / Satış Karşılaştırması
                                                        </h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <Chart options={temsilciOptions} series={temsilciSeries} type="bar" height={320} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-xl-4">
                                                <div className="card border-0 shadow-sm h-100">
                                                    <div className="card-header bg-white">
                                                        <h6 className="card-title mb-0 fw-semibold">
                                                            <i className="feather-award text-warning me-2"></i>
                                                            Dönüşüm Sıralaması
                                                        </h6>
                                                    </div>
                                                    <div className="card-body p-0">
                                                        <ul className="list-group list-group-flush">
                                                            {[...analytics.agentPerformance]
                                                                .sort((a, b) => {
                                                                    const ca = a.atanan_lead > 0 ? a.satis / a.atanan_lead : 0;
                                                                    const cb = b.atanan_lead > 0 ? b.satis / b.atanan_lead : 0;
                                                                    return cb - ca;
                                                                })
                                                                .map((a, i) => {
                                                                    const conv = a.atanan_lead > 0 ? ((a.satis / a.atanan_lead) * 100).toFixed(1) : 0;
                                                                    return (
                                                                        <li key={i} className="list-group-item border-0 px-4 py-3">
                                                                            <div className="d-flex align-items-center gap-3">
                                                                                <div className="fw-bold" style={{ color: i === 0 ? '#f59e0b' : i === 1 ? '#9ca3af' : i === 2 ? '#cd7c32' : '#d1d5db', width: 20, fontSize: 16 }}>
                                                                                    {i + 1}
                                                                                </div>
                                                                                <div className="flex-grow-1">
                                                                                    <div className="fw-semibold small">{a.temsilci}</div>
                                                                                    <div className="small text-muted">{fmt(a.atanan_lead)} lead · {fmt(a.satis)} satış</div>
                                                                                </div>
                                                                                <span className={`badge fw-semibold ${conv >= 10 ? 'bg-success' : conv >= 5 ? 'bg-warning text-dark' : 'bg-light text-danger border'}`}>
                                                                                    %{conv}
                                                                                </span>
                                                                            </div>
                                                                        </li>
                                                                    );
                                                                })}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Detaylı Tablo */}
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white">
                                                <h6 className="card-title mb-0 fw-semibold">
                                                    <i className="feather-table text-muted me-2"></i>
                                                    Detaylı Temsilci Tablosu
                                                </h6>
                                            </div>
                                            <div className="card-body p-0">
                                                <div className="table-responsive">
                                                    <table className="table table-hover mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th className="ps-4 py-3 small fw-semibold">#</th>
                                                                <th className="py-3 small fw-semibold">TEMSİLCİ</th>
                                                                <th className="py-3 small fw-semibold text-end">ATANAN LEAD</th>
                                                                <th className="py-3 small fw-semibold text-end">TEKLİF</th>
                                                                <th className="py-3 small fw-semibold text-end">SATIŞ</th>
                                                                <th className="py-3 small fw-semibold text-end">TEKLİF ORANI</th>
                                                                <th className="pe-4 py-3 small fw-semibold text-end">DÖNÜŞÜM</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {analytics.agentPerformance.map((a, i) => {
                                                                const conv = a.atanan_lead > 0 ? ((a.satis / a.atanan_lead) * 100).toFixed(1) : 0;
                                                                const tekOr = a.atanan_lead > 0 ? ((a.teklif / a.atanan_lead) * 100).toFixed(1) : 0;
                                                                return (
                                                                    <tr key={i}>
                                                                        <td className="ps-4 py-3 text-muted">{i + 1}</td>
                                                                        <td className="py-3 fw-semibold">{a.temsilci}</td>
                                                                        <td className="py-3 text-end">{fmt(a.atanan_lead)}</td>
                                                                        <td className="py-3 text-end text-primary">{fmt(a.teklif)}</td>
                                                                        <td className="py-3 text-end text-success fw-bold">{fmt(a.satis)}</td>
                                                                        <td className="py-3 text-end">
                                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                                <div className="progress flex-grow-1" style={{ height: 6, maxWidth: 80 }}>
                                                                                    <div className="progress-bar bg-primary" style={{ width: Math.min(tekOr, 100) + '%' }}></div>
                                                                                </div>
                                                                                <span className="small fw-semibold">%{tekOr}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="pe-4 py-3 text-end">
                                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                                <div className="progress flex-grow-1" style={{ height: 6, maxWidth: 80 }}>
                                                                                    <div className={`progress-bar ${conv >= 10 ? 'bg-success' : conv >= 5 ? 'bg-warning' : 'bg-danger'}`}
                                                                                        style={{ width: Math.min(conv, 100) + '%' }}></div>
                                                                                </div>
                                                                                <span className={`badge small ${conv >= 10 ? 'bg-success' : conv >= 5 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                                                                    %{conv}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center text-muted py-5">
                                        <i className="feather-users fs-1 d-block mb-2"></i>
                                        <p>Temsilci verisi bulunamadı</p>
                                    </div>
                                )}
                            </>
                        )}

                        {/* ════ SEKTÖR ANALİZİ ════ */}
                        {activeTab === 'sektor' && isSectionVisible('sektor') && analytics?.sektorPerf?.length > 0 && (
                            <>
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white">
                                        <h6 className="card-title mb-0 fw-semibold">
                                            <i className="feather-briefcase text-warning me-2"></i>
                                            Sektör Bazlı Lead Dağılımı
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <Chart
                                            options={{
                                                chart: { toolbar: { show: false }, fontFamily: 'inherit' },
                                                plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true } },
                                                xaxis: { labels: { style: { fontSize: '11px' } } },
                                                yaxis: { categories: analytics.sektorPerf.map(s => s.sektor), labels: { style: { fontSize: '11px' } } },
                                                colors: ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#6366f1','#ec4899','#06b6d4','#84cc16','#f97316','#a855f7','#14b8a6','#f43f5e','#0ea5e9','#d97706'],
                                                legend: { show: false },
                                                dataLabels: { enabled: true, style: { fontSize: '11px' } },
                                                grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
                                                tooltip: { y: { formatter: v => fmt(v) + ' lead' } }
                                            }}
                                            series={[{ name: 'Lead Sayısı', data: analytics.sektorPerf.map(s => s.toplam) }]}
                                            type="bar"
                                            height={Math.max(300, analytics.sektorPerf.length * 36 + 60)}
                                        />
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white">
                                        <h6 className="card-title mb-0 fw-semibold">
                                            <i className="feather-briefcase text-warning me-2"></i>
                                            Sektör × Pipeline Tablosu
                                        </h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="ps-4 py-3 fw-semibold small">SEKTÖR</th>
                                                        {pipelineBreakdown.map(stage => (
                                                            <th key={stage.Asama_ID} className="py-3 fw-semibold small text-center">
                                                                <span className="badge" style={{ background: stage.Renk || '#6366f1', color: '#fff', fontSize: '10px' }}>
                                                                    {stage.Ad}
                                                                </span>
                                                            </th>
                                                        ))}
                                                        <th className="pe-4 py-3 fw-semibold small text-end">TOPLAM</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {analytics.sektorPerf.map((sektor, i) => (
                                                        <tr key={i}>
                                                            <td className="ps-4 py-3 fw-medium">{sektor.sektor}</td>
                                                            {pipelineBreakdown.map(stage => (
                                                                <td key={stage.Asama_ID} className="py-3 text-center">
                                                                    {sektor.stages[stage.Asama_ID] || 0}
                                                                </td>
                                                            ))}
                                                            <td className="pe-4 py-3 text-end fw-bold text-primary">{sektor.toplam}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ════ AI RAPORU ════ */}
                        {activeTab === 'ai' && (
                            <div>
                                {/* Header kartı */}
                                <div className="card border-0 mb-4" style={{
                                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                                    borderRadius: 16
                                }}>
                                    <div className="card-body p-4 d-flex align-items-center gap-4">
                                        <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                            style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.2)', fontSize: 28 }}>
                                            ✨
                                        </div>
                                        <div className="text-white">
                                            <h5 className="fw-bold mb-1">Gemini AI Raporu</h5>
                                            <p className="mb-0 opacity-75 small">
                                                Kampanya, etiket ve satış verileriniz Gemini 2.0 Flash tarafından analiz edildi.
                                                <span className="ms-2 badge" style={{ background: 'rgba(255,255,255,0.25)' }}>
                                                    {startDate && lastDate
                                                        ? `${startDate} → ${lastDate}`
                                                        : 'Tüm zamanlar'}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="ms-auto d-flex gap-2">
                                            <button
                                                className="btn btn-sm"
                                                style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                                                onClick={generateAiReport}
                                                disabled={aiLoading}
                                            >
                                                {aiLoading
                                                    ? <><span className="spinner-border spinner-border-sm me-1"></span>Yenileniyor...</>
                                                    : <><i className="feather-refresh-cw me-1"></i>Yenile</>
                                                }
                                            </button>
                                            {aiReport && (
                                                <button
                                                    className="btn btn-sm"
                                                    style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                                                    onClick={() => window.print()}
                                                >
                                                    <i className="feather-printer me-1"></i>Yazdır
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* İçerik */}
                                {aiLoading ? (
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body py-5 text-center">
                                            <div className="mb-4">
                                                <div className="spinner-grow text-purple mx-1" style={{ width: '0.6rem', height: '0.6rem', background: '#8b5cf6', animationDelay: '0s' }}></div>
                                                <div className="spinner-grow mx-1" style={{ width: '0.6rem', height: '0.6rem', background: '#a855f7', animationDelay: '0.15s' }}></div>
                                                <div className="spinner-grow mx-1" style={{ width: '0.6rem', height: '0.6rem', background: '#6366f1', animationDelay: '0.3s' }}></div>
                                            </div>
                                            <h6 className="fw-semibold mb-1" style={{ color: '#6366f1' }}>Gemini verilerinizi analiz ediyor...</h6>
                                            <p className="text-muted small mb-0">Kampanyalar, etiketler ve satış verileri işleniyor. Bu birkaç saniye sürebilir.</p>
                                        </div>
                                    </div>
                                ) : aiReport ? (
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body p-4">
                                            <MarkdownText text={aiReport} />
                                        </div>
                                        <div className="card-footer bg-light border-0 d-flex align-items-center gap-2 py-2 px-4">
                                            <span className="badge" style={{ background: '#6366f120', color: '#6366f1' }}>
                                                <i className="feather-zap me-1"></i>Gemini 2.0 Flash
                                            </span>
                                            <span className="small text-muted">
                                                {campaignData ? `${campaignData.length} kampanya · ` : ''}
                                                {analytics?.sourceBreakdown?.length ?? 0} kaynak ·
                                                {analytics?.agentPerformance?.length ?? 0} temsilci analiz edildi
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body py-5 text-center text-muted">
                                            <i className="feather-zap fs-1 d-block mb-3" style={{ color: '#c4b5fd' }}></i>
                                            <h6 className="fw-semibold mb-2">AI Raporu Henüz Oluşturulmadı</h6>
                                            <p className="small mb-3">Filtrelerinizi ayarlayıp <strong>"AI Raporu"</strong> butonuna basın.</p>
                                            <button className="btn btn-sm px-4"
                                                style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none' }}
                                                onClick={generateAiReport}>
                                                <i className="feather-zap me-1"></i>AI Raporu Oluştur
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ════ KAMPANYA ANALİZİ ════ */}
                        {activeTab === 'campaign' && isSectionVisible('kampanya') && campaignData && (() => {
                            const totalLeads = campaignData.reduce((s, c) => s + c.toplam, 0);
                            const totalSales = campaignData.reduce((s, c) => s + c.satis, 0);
                            const bestCampaign = [...campaignData].sort((a, b) =>
                                (b.toplam > 0 ? b.satis / b.toplam : 0) - (a.toplam > 0 ? a.satis / a.toplam : 0)
                            )[0];

                            return (
                                <>
                                    {/* KPI Kartları */}
                                    <div className="row g-3 mb-4">
                                        <div className="col-6 col-md-3">
                                            <div className="card border-0 shadow-sm text-center py-3 px-2">
                                                <div className="fs-3 fw-bold text-primary">{campaignData.length}</div>
                                                <div className="small text-muted mt-1">Aktif Kampanya</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="card border-0 shadow-sm text-center py-3 px-2">
                                                <div className="fs-3 fw-bold text-info">{fmt(totalLeads)}</div>
                                                <div className="small text-muted mt-1">Toplam Lead</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="card border-0 shadow-sm text-center py-3 px-2">
                                                <div className="fs-3 fw-bold text-success">{fmt(totalSales)}</div>
                                                <div className="small text-muted mt-1">Toplam Satış</div>
                                                <div className="small fw-semibold text-success">{pct(totalSales, totalLeads)} dönüşüm</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="card border-0 shadow-sm text-center py-3 px-2">
                                                <div className="fw-bold text-warning" style={{ fontSize: 14 }}>
                                                    {bestCampaign ? (bestCampaign.kampanya || '—').substring(0, 24) : '—'}
                                                </div>
                                                <div className="small text-muted mt-1">En İyi Dönüşüm</div>
                                                {bestCampaign && (
                                                    <div className="small fw-bold text-warning">
                                                        {pct(bestCampaign.satis, bestCampaign.toplam)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bar Grafik */}
                                    {topCampaigns.length > 0 && (
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-header bg-white border-bottom-0 pb-0">
                                                <h6 className="card-title mb-0 fw-semibold">
                                                    <i className="feather-bar-chart-2 text-primary me-2"></i>
                                                    Kampanya Bazlı Lead & Satış
                                                    <small className="text-muted fw-normal ms-2">(İlk {topCampaigns.length} kampanya)</small>
                                                </h6>
                                            </div>
                                            <div className="card-body pt-2">
                                                <Chart
                                                    options={kampanyaChartOptions}
                                                    series={kampanyaChartSeries}
                                                    type="bar"
                                                    height={Math.max(200, topCampaigns.length * 36 + 60)}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Kampanya Detay Tablosu */}
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-header bg-white">
                                            <h6 className="card-title mb-0 fw-semibold">
                                                <i className="feather-layers text-warning me-2"></i>
                                                Kampanya Detayları — Etiket Dağılımı
                                            </h6>
                                        </div>
                                        <div className="card-body p-0">
                                            {campaignData.length === 0 ? (
                                                <div className="text-center py-5 text-muted">
                                                    <i className="feather-inbox fs-1 d-block mb-2"></i>
                                                    Seçilen filtrede kampanya verisi bulunamadı
                                                </div>
                                            ) : (
                                                campaignData.map((camp, ci) => {
                                                    const donusum = camp.toplam > 0
                                                        ? ((camp.satis / camp.toplam) * 100).toFixed(1)
                                                        : 0;
                                                    const isExpanded = expandedCampaign === camp.kampanya;

                                                    return (
                                                        <div key={ci} className={`border-bottom ${ci === campaignData.length - 1 ? 'border-bottom-0' : ''}`}>
                                                            {/* Kampanya Satırı */}
                                                            <div className="d-flex align-items-start gap-3 p-3 flex-wrap">
                                                                {/* Kampanya adı + kaynak */}
                                                                <div style={{ minWidth: 200, flex: '1 1 200px' }}>
                                                                    <div className="fw-semibold text-dark" style={{ fontSize: 13 }}>
                                                                        {camp.kampanya || <span className="text-muted fst-italic">İsimsiz</span>}
                                                                    </div>
                                                                    <span className="badge bg-light text-dark mt-1" style={{ fontSize: 11 }}>
                                                                        {camp.kaynak || 'Bilinmiyor'}
                                                                    </span>
                                                                </div>

                                                                {/* İstatistikler */}
                                                                <div className="d-flex gap-4 flex-wrap align-items-center" style={{ flex: '0 0 auto' }}>
                                                                    <div className="text-center">
                                                                        <div className="fw-bold fs-6 text-primary">{fmt(camp.toplam)}</div>
                                                                        <div className="small text-muted">Lead</div>
                                                                    </div>
                                                                    <div className="text-center">
                                                                        <div className="fw-bold fs-6 text-success">{fmt(camp.satis)}</div>
                                                                        <div className="small text-muted">Satış</div>
                                                                    </div>
                                                                    <div className="text-center">
                                                                        <span className={`badge fs-6 px-2 py-1 ${donusum >= 10 ? 'bg-success' : donusum >= 5 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                                                            %{donusum}
                                                                        </span>
                                                                        <div className="small text-muted">Dönüşüm</div>
                                                                    </div>
                                                                </div>

                                                                {/* Etiket dağılımı */}
                                                                <div style={{ flex: '1 1 260px' }}>
                                                                    {camp.etiketler.length > 0 ? (
                                                                        <div className="d-flex flex-wrap gap-1">
                                                                            {camp.etiketler.slice(0, 6).map(e => (
                                                                                <span key={e.id} className="badge" style={{
                                                                                    background: '#' + e.renk,
                                                                                    color: '#' + e.yaziRenk,
                                                                                    fontSize: 11,
                                                                                    padding: '4px 8px'
                                                                                }}>
                                                                                    {e.adi}
                                                                                    <span className="ms-1 fw-bold">{e.adet}</span>
                                                                                </span>
                                                                            ))}
                                                                            {camp.etiketler.length > 6 && (
                                                                                <span className="badge bg-light text-muted" style={{ fontSize: 11 }}>
                                                                                    +{camp.etiketler.length - 6} daha
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="small text-muted fst-italic">Etiket yok</span>
                                                                    )}
                                                                </div>

                                                                {/* Genişlet butonu */}
                                                                {camp.satisOlmayanlar.length > 0 && (
                                                                    <button
                                                                        className={`btn btn-sm ms-auto ${isExpanded ? 'btn-danger' : 'btn-outline-danger'}`}
                                                                        onClick={() => setExpandedCampaign(isExpanded ? null : camp.kampanya)}
                                                                        style={{ whiteSpace: 'nowrap' }}
                                                                    >
                                                                        <i className={`feather-${isExpanded ? 'chevron-up' : 'chevron-down'} me-1`}></i>
                                                                        Satışa Dönmeyenler ({camp.satisOlmayanlar.length})
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {/* Satışa Dönmeyenler Paneli */}
                                                            {isExpanded && (
                                                                <div className="mx-3 mb-3 rounded-3 overflow-hidden border border-danger border-opacity-25 bg-danger bg-opacity-10">
                                                                    <div className="px-3 py-2 d-flex align-items-center justify-content-between" style={{ background: 'rgba(239,68,68,0.08)' }}>
                                                                        <span className="fw-semibold small text-danger">
                                                                            <i className="feather-x-circle me-1"></i>
                                                                            Satışa Dönmeyen Leadler
                                                                            {camp.satisOlmayanlar.length > 50 && (
                                                                                <span className="text-muted fw-normal ms-1">(İlk 50 gösteriliyor)</span>
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                    <div className="table-responsive">
                                                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: 12 }}>
                                                                            <thead style={{ background: 'rgba(239,68,68,0.06)' }}>
                                                                                <tr>
                                                                                    <th className="ps-3 py-2 fw-semibold">ADI SOYADI</th>
                                                                                    <th className="py-2 fw-semibold">TELEFON</th>
                                                                                    <th className="py-2 fw-semibold">TARİH</th>
                                                                                    <th className="py-2 fw-semibold">AÇIKLAMA</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {camp.satisOlmayanlar.slice(0, 50).map((lead, li) => {
                                                                                    const d = lead.tarih ? new Date(lead.tarih) : null;
                                                                                    const dateStr = d
                                                                                        ? `${d.getDate().toString().padStart(2,'0')}.${(d.getMonth()+1).toString().padStart(2,'0')}.${d.getFullYear()}`
                                                                                        : '—';
                                                                                    const shortDesc = lead.aciklama
                                                                                        ? (lead.aciklama.length > 55 ? lead.aciklama.substring(0, 55) + '…' : lead.aciklama)
                                                                                        : null;
                                                                                    return (
                                                                                        <tr key={li}>
                                                                                            <td className="ps-3 py-2 fw-medium">{lead.adi_soyadi || '—'}</td>
                                                                                            <td className="py-2">
                                                                                                <a href={`tel:${lead.telefon}`} className="text-dark text-decoration-none">{lead.telefon || '—'}</a>
                                                                                            </td>
                                                                                            <td className="py-2 text-muted">{dateStr}</td>
                                                                                            <td className="py-2" style={{ maxWidth: 280 }}>
                                                                                                {lead.aciklama ? (
                                                                                                    <Tooltip content={lead.aciklama}>
                                                                                                        <span style={{
                                                                                                            cursor: 'help',
                                                                                                            borderBottom: '1px dashed #94a3b8',
                                                                                                            color: '#374151'
                                                                                                        }}>
                                                                                                            {shortDesc}
                                                                                                        </span>
                                                                                                    </Tooltip>
                                                                                                ) : (
                                                                                                    <span className="text-muted fst-italic">Açıklama yok</span>
                                                                                                )}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </>
                            );
                        })()}

                        {/* ════ LEAD LİSTESİ ════ */}
                        {activeTab === 'detail' && isSectionVisible('detail') && showLeadsTable && (
                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <h6 className="card-title mb-0 fw-semibold">
                                            <i className="feather-list text-primary me-2"></i>
                                            Lead Listesi
                                        </h6>
                                        <span className="badge bg-primary">{filteredLeads.length}</span>
                                        {filteredLeads.length !== leads.length && (
                                            <span className="badge bg-secondary">{leads.length} toplam</span>
                                        )}
                                    </div>
                                    {filteredLeads.length > 0 && (
                                        <button className="btn btn-success btn-sm" onClick={excelDownload}>
                                            <i className="feather-download me-1"></i>Excel İndir ({filteredLeads.length})
                                        </button>
                                    )}
                                </div>
                                <div className="card-body p-0">
                                    <DataTable
                                        columns={columns}
                                        data={filteredLeads}
                                        pagination
                                        responsive
                                        noDataComponent={
                                            <div className="text-center py-5 text-muted">
                                                <i className="feather-inbox fs-1 d-block mb-2"></i>
                                                Seçilen filtrelere uygun kayıt bulunamadı
                                            </div>
                                        }
                                        paginationComponentOptions={paginationOpts}
                                        paginationPerPage={25}
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                        customStyles={{
                                            rows: { style: { padding: '12px 0' } },
                                            headRow: { style: { background: '#f8fafc' } }
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </Layout>
    );
};

export default Reports;
